import moment from 'moment-timezone';
import type { SimulateShiftChangesQueryCgResponse } from '@common/graphql/schema';

type SimulationResultType =
  SimulateShiftChangesQueryCgResponse['simulateShiftChanges'][number];

type ShiftStatusType = 'new' | 'change' | 'close';

type ShiftGroupType = {
  key: string;
  status: ShiftStatusType;
  icon: 'sun' | 'moon';
  startHour: number;
  endHour: number;
  duration: number;
  prevCount: number;
  headcount: number;
  diffCount: number;
  unitName: string;
  locationName: string;
  roleName: string;
  children: ShiftChildType[];
};

type ShiftChildType = {
  key: string;
  status: ShiftStatusType;
  icon: 'sun' | 'moon';
  startHour: number;
  endHour: number;
  duration: number;
  unitName: string;
  locationName: string;
  roleName: string;
  prevCount: number;
  openings: number;
  diffCount: number;
};

export type PreviewDataType = {
  shiftGroups: ShiftGroupType[];
  createdOpenings: number;
  closedOpenings: number;
  totalShifts: number;
};

function hourInZone(datetime: string, timeZone: string): number {
  return moment.tz(datetime, timeZone).hour();
}

function isContainedWithin(
  child: SimulationResultType,
  parent: SimulationResultType
): boolean {
  const childStart = moment(child.startDatetime).valueOf();
  const childEnd = moment(child.endDatetime).valueOf();
  const parentStart = moment(parent.startDatetime).valueOf();
  const parentEnd = moment(parent.endDatetime).valueOf();

  return childStart >= parentStart && childEnd <= parentEnd;
}

function iconForResult(result: SimulationResultType): 'sun' | 'moon' {
  return result.timeOfDay === 'night' ? 'moon' : 'sun';
}

function statusForResult(result: SimulationResultType): ShiftStatusType {
  if (result.prevCount === 0) return 'new';
  if (result.newCount === 0) return 'close';
  return 'change';
}

function countOpenings(changed: SimulationResultType[]) {
  let createdOpenings = 0;
  let closedOpenings = 0;
  for (const r of changed) {
    if (r.diffCount > 0) createdOpenings += r.diffCount;
    else closedOpenings += Math.abs(r.diffCount);
  }
  return { createdOpenings, closedOpenings };
}

export function transformSimulationResults(
  results: SimulationResultType[]
): PreviewDataType {
  const changed = results.filter((r) => r.diffCount !== 0);
  const sorted = [...changed].sort((a, b) => b.durationHours - a.durationHours);
  const assigned = new Set<number>();
  const shiftGroups: ShiftGroupType[] = [];

  for (let i = 0; i < sorted.length; i++) {
    if (assigned.has(i)) continue;

    const parent = sorted[i];
    assigned.add(i);

    const icon = iconForResult(parent);
    const tz = parent.timeZone;
    const children: ShiftChildType[] = [];

    for (let j = i + 1; j < sorted.length; j++) {
      if (assigned.has(j)) continue;
      if (!isContainedWithin(sorted[j], parent)) continue;

      const item = sorted[j];
      assigned.add(j);

      children.push({
        key: `${item.organizationUnit.name}-${item.role.name}-${item.startDatetime}`,
        status: statusForResult(item),
        icon: iconForResult(item),
        startHour: hourInZone(item.startDatetime, tz),
        endHour: hourInZone(item.endDatetime, tz),
        duration: item.durationHours,
        unitName: item.organizationUnit.name,
        locationName: item.organizationUnit.organizationBusiness.name,
        roleName: item.role.abbreviation || item.role.name,
        prevCount: item.prevCount,
        openings: item.newCount,
        diffCount: item.diffCount,
      });
    }

    shiftGroups.push({
      key: `group-${parent.organizationUnit.name}-${parent.role.name}-${parent.startDatetime}`,
      status: statusForResult(parent),
      icon,
      startHour: hourInZone(parent.startDatetime, tz),
      endHour: hourInZone(parent.endDatetime, tz),
      duration: parent.durationHours,
      prevCount: parent.prevCount,
      headcount: parent.newCount,
      diffCount: parent.diffCount,
      unitName: parent.organizationUnit.name,
      locationName: parent.organizationUnit.organizationBusiness.name,
      roleName: parent.role.abbreviation || parent.role.name,
      children,
    });
  }

  const { createdOpenings, closedOpenings } = countOpenings(changed);

  return {
    shiftGroups,
    createdOpenings,
    closedOpenings,
    totalShifts: changed.length,
  };
}
